import { Injectable, computed, signal } from '@angular/core';
import { MessageService } from 'primeng/api';

/** Severity of a notification — mirrors PrimeNG's toast severities. */
export type NotificationSeverity = 'success' | 'info' | 'warn' | 'error';

/** A single notification, both toasted and kept in the header bell's history. */
export interface AppNotification {
  id: number;
  severity: NotificationSeverity;
  summary: string;
  detail: string;
  timestamp: Date;
}

/**
 * Central place to raise user-facing notifications.
 *
 * Every notification does two things: it pops a PrimeNG toast (styled by the
 * Figma-derived theme) and it's added to a running history surfaced by the
 * header's notification bell. Add new `success`/`info`/… calls from anywhere a
 * user action completes.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly items = signal<AppNotification[]>([]);
  private nextId = 1;

  /** All notifications, most recent first. */
  readonly notifications = this.items.asReadonly();
  /** Number of notifications in the history (drives the bell badge). Cleared only by {@link clear}. */
  readonly count = computed(() => this.items().length);

  constructor(private readonly messages: MessageService) {}

  /** Raises a green success notification (e.g. after an export completes). */
  success(summary: string, detail: string): void {
    this.push('success', summary, detail);
  }

  /** Clears the notification history — the only thing that resets the bell badge. */
  clear(): void {
    this.items.set([]);
  }

  private push(severity: NotificationSeverity, summary: string, detail: string): void {
    this.messages.add({ severity, summary, detail, life: 4000 });
    this.items.update((list) => [
      { id: this.nextId++, severity, summary, detail, timestamp: new Date() },
      ...list
    ]);
  }
}
